/**
 * ActivityTimeline - TASK-378
 * Chronological list of compliance events.
 * Each entry shows: event icon, description, timestamp, user, severity badge.
 * Filterable by event_type.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity,
  FileText,
  ShieldCheck,
  UserCheck,
  Upload,
  ClipboardCheck,
  AlertTriangle,
  Send,
  Clock,
  Filter,
  ChevronDown,
} from 'lucide-react';
import { useComplianceContextOptional } from '../../../contexts/ComplianceContext';
import { getClientActivity } from '../../../services/complianceActivityService';
import { formatTimestamp } from '../../../utils/formatters';
import type {
  ComplianceActivity,
  ComplianceEventType,
  ActivitySeverity,
} from '../../../types/complianceWorkflow';

const EVENT_ICON_MAP: Record<string, React.ElementType> = {
  kyc_submitted: Send,
  kyc_review_assigned: ClipboardCheck,
  kyc_review_approved: ShieldCheck,
  kyc_review_rejected: AlertTriangle,
  kyc_changes_requested: FileText,
  document_requested: FileText,
  document_uploaded: Upload,
  document_fulfilled: FileText,
  client_profile_generated: UserCheck,
  form_a_generated: FileText,
  risk_profile_started: Activity,
  risk_profile_completed: ShieldCheck,
};

const SEVERITY_BADGE: Record<ActivitySeverity, { bg: string; text: string }> = {
  info: { bg: 'bg-blue-100', text: 'text-blue-700' },
  warning: { bg: 'bg-amber-100', text: 'text-amber-700' },
  error: { bg: 'bg-red-100', text: 'text-red-600' },
  critical: { bg: 'bg-red-200', text: 'text-red-800' },
};

const EVENT_TYPE_LABELS: Record<ComplianceEventType, string> = {
  kyc_submitted: 'KYC Submitted',
  kyc_review_assigned: 'Review Assigned',
  kyc_review_approved: 'Review Approved',
  kyc_review_rejected: 'Review Rejected',
  kyc_changes_requested: 'Changes Requested',
  document_requested: 'Doc Requested',
  document_uploaded: 'Doc Uploaded',
  document_fulfilled: 'Doc Fulfilled',
  client_profile_generated: 'Profile Generated',
  form_a_generated: 'Form A Generated',
  risk_profile_started: 'Risk Started',
  risk_profile_completed: 'Risk Completed',
};

const ALL_EVENT_TYPES: ComplianceEventType[] = [
  'kyc_submitted',
  'kyc_review_assigned',
  'kyc_review_approved',
  'kyc_review_rejected',
  'kyc_changes_requested',
  'document_requested',
  'document_uploaded',
  'document_fulfilled',
  'client_profile_generated',
  'form_a_generated',
  'risk_profile_started',
  'risk_profile_completed',
];

export const ActivityTimeline: React.FC<{ clientId?: string }> = ({ clientId: clientIdProp }) => {
  const ctx = useComplianceContextOptional();
  const clientId = clientIdProp ?? ctx?.clientId;
  const [activities, setActivities] = useState<ComplianceActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<ComplianceEventType | ''>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchActivities = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const params: { limit: number; offset: number; event_type?: string } = {
        limit: 20,
        offset: 0,
      };
      if (selectedFilter) {
        params.event_type = selectedFilter;
      }

      const response = await getClientActivity(clientId, params);
      setActivities(response?.items ?? []);
      setTotal(response?.total ?? 0);
    } catch {
      setActivities([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedFilter]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Activity Timeline</h3>
          {total > 0 && (
            <span className="text-xs text-gray-400">({total})</span>
          )}
        </div>

        {/* Filter dropdown */}
        <div className="relative">
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <Filter className="w-3 h-3" />
            {selectedFilter ? EVENT_TYPE_LABELS[selectedFilter] : 'All Events'}
            <ChevronDown className="w-3 h-3" />
          </button>

          {filterOpen && (
            <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 max-h-60 overflow-y-auto">
              <button
                onClick={() => { setSelectedFilter(''); setFilterOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                  !selectedFilter ? 'text-kielis-navy font-medium' : 'text-gray-600'
                }`}
              >
                All Events
              </button>
              {ALL_EVENT_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => { setSelectedFilter(type); setFilterOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                    selectedFilter === type ? 'text-kielis-navy font-medium' : 'text-gray-600'
                  }`}
                >
                  {EVENT_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="divide-y divide-gray-100">
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 animate-pulse">
                <div className="w-8 h-8 bg-gray-200 rounded-full shrink-0" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">
            No activity recorded yet
          </div>
        ) : (
          activities.map((activity) => {
            const EventIcon = EVENT_ICON_MAP[activity.event_type] || Activity;
            const severityConfig = SEVERITY_BADGE[activity.severity];

            return (
              <div key={activity.id} className="px-4 py-3 flex items-start gap-3">
                {/* Icon */}
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                  <EventIcon className="w-4 h-4 text-gray-500" />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {/* Severity badge */}
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${severityConfig.bg} ${severityConfig.text}`}
                    >
                      {activity.severity}
                    </span>

                    {/* User */}
                    {activity.user_name && (
                      <span className="text-xs text-gray-500">
                        by {activity.user_name}
                      </span>
                    )}

                    {/* Timestamp */}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {activity.created_at ? formatTimestamp(activity.created_at) : ''}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;
